import { z } from "zod";
import {
  TEXT_AGGREGATES,
  compareText,
  fail,
  ok,
  textOperators,
  type FieldKind,
} from "../kind";

export const textSettings = z.object({
  placeholder: z.string().max(200).optional(),
  maxLength: z.number().int().min(1).max(10_000).optional(),
  // Dans la grille : sur une ligne (tronqué) ou retour à la ligne.
  wrap: z.boolean().default(false),
});

/** Plafond d'un texte court, réglage ou non : au-delà, c'est un rich_text. */
export const TEXT_HARD_MAX = 10_000;

/** Un scalaire en texte : nombres et booléens oui, objets non. */
export function scalarToText(input: unknown): string | null {
  if (typeof input === "string") return input;
  if (typeof input === "number" && Number.isFinite(input)) return String(input);
  if (typeof input === "boolean") return input ? "true" : "false";
  if (Array.isArray(input) && input.every((item) => scalarToText(item) !== null)) {
    return input.map((item) => scalarToText(item)).join(", ");
  }
  return null;
}

export const textKind: FieldKind<"text"> = {
  type: "text",
  settings: textSettings,
  value: z.string().min(1).max(TEXT_HARD_MAX),
  coerce(input, settings) {
    const raw = scalarToText(input);
    if (raw === null) return fail("Expected text.");
    // Un texte court tient sur une ligne : les retours deviennent des espaces.
    const text = raw.replace(/\s*[\r\n]+\s*/g, " ").trim();
    if (text === "") return ok(null);
    const max = Math.min(settings.maxLength ?? TEXT_HARD_MAX, TEXT_HARD_MAX);
    if (text.length > max) return fail(`Text is too long (${max} characters at most).`);
    return ok(text);
  },
  format: (value) => value,
  compare: (a, b) => compareText(a, b),
  filters: textOperators<"text">((value) => value),
  aggregates: TEXT_AGGREGATES,
};
